package com.evoapps.evoshift.ui.util

import androidx.compose.runtime.Composable
import com.evoapps.evoshift.domain.util.calculateDurationMinutes
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.duration_hours_part
import evoshift.composeapp.generated.resources.duration_minutes_part
import org.jetbrains.compose.resources.pluralStringResource

/*
 * SH-190: shift template lengths to the minute ("7 h 30 min", "8 h", "45 min"; in Polish
 * "7 godzin 30 minut"), not rounded to whole hours.
 */

/** Whole hours and the minutes left over (negative lengths count as 0). */
data class DurationParts(val hours: Int, val minutes: Int)

fun durationParts(totalMinutes: Int): DurationParts {
    val minutes = totalMinutes.coerceAtLeast(0)
    return DurationParts(minutes / 60, minutes % 60)
}

/**
 * The length in words, with [hoursText] / [minutesText] giving one unit ("7 h", "30 min"): hours
 * only when there are no minutes, minutes only under an hour, both otherwise; 0 is "0 h".
 */
fun formatDuration(totalMinutes: Int, hoursText: (Int) -> String, minutesText: (Int) -> String): String {
    val (hours, minutes) = durationParts(totalMinutes)
    return when {
        minutes == 0 -> hoursText(hours)
        hours == 0 -> minutesText(minutes)
        else -> "${hoursText(hours)} ${minutesText(minutes)}"
    }
}

/** [totalMinutes] in the app's language, e.g. "7 h 30 min". */
@Composable
fun durationText(totalMinutes: Int): String {
    val (hours, minutes) = durationParts(totalMinutes)
    val hoursText = pluralStringResource(Res.plurals.duration_hours_part, hours, hours)
    val minutesText = pluralStringResource(Res.plurals.duration_minutes_part, minutes, minutes)
    return formatDuration(totalMinutes, { hoursText }, { minutesText })
}

/** A template's length from its clock times; over midnight (22:00–06:00) counts into the next day. */
@Composable
fun templateDurationText(startTime: String, endTime: String): String =
    durationText(calculateDurationMinutes(startTime, endTime))
